import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DemoModeContextType {
  demoMode: boolean;
  setDemoMode: (enabled: boolean) => void;
  toggleDemoMode: () => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

interface DemoModeProviderProps {
  children: ReactNode;
}

export function DemoModeProvider({ children }: DemoModeProviderProps) {
  const [demoMode, setDemoModeState] = useState<boolean>(() => {
    // Initialize from localStorage or default to true
    const stored = localStorage.getItem('demoMode');
    if (stored !== null) {
      return JSON.parse(stored);
    }
    return true; // Default to demo mode
  });

  const setDemoMode = (enabled: boolean) => {
    setDemoModeState(enabled);
    localStorage.setItem('demoMode', JSON.stringify(enabled));
    // Also set on window for global access
    (window as any).DEMO_MODE = enabled;
  };

  const toggleDemoMode = () => {
    setDemoMode(!demoMode);
  };

  useEffect(() => {
    // Set initial value on window
    (window as any).DEMO_MODE = demoMode;
  }, [demoMode]);

  return (
    <DemoModeContext.Provider value={{ demoMode, setDemoMode, toggleDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode(): DemoModeContextType {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}